import * as React from "react";
import { Redirect } from "react-router-dom";
import Loader from "react-loader";
import { ExpandableTable } from "@quid/react-expandable-table";
import { toast, ToastContainer } from "react-toastify";

import withServices from "../../../../HOCs/withServices";
import Navbar from "../../../../components/CustomNav/CustomNav.Component";
import BubbleChart from "../../../../common/DataViz/bubbleChart.Component";
import PieChart from "../../../../common/DataViz/pie.Component";

import "./searchFilters.css";

interface IProps {
  services?: any;
}

class TraitsAnalysis extends React.Component<IProps> {
  public state = {
    toLogin: false,
    loaded: true,

    assessments: new Array(),
    selectedAssessment: "0",
    results: new Array(),
    relations: new Array(),
    traitsFilters: {},
    usersFilters: {},
    selectedTraits: new Array(),
    selectedUsers: new Array(),
    selectedUsersResults: {},
    openedRows: new Array(),
    loading: false,
  };

  constructor(props) {
    super(props);

    this.onLogout = this.onLogout.bind(this);
    this.onToggleRow = this.onToggleRow.bind(this);
    this.renderTableRow = this.renderTableRow.bind(this);
  }

  public async componentDidMount() {
    const {
      services: { adminAPI, isUserLoggedIn },
    } = this.props;

    if (!isUserLoggedIn) {
      this.setState({ toLogin: true });
    }

    const {
      data: { assessments },
    } = await adminAPI.getUserAssessments();

    this.setState({ assessments });
  }

  public onLogout() {
    this.setState({ toLogin: true });
  }

  public onToggleRow(openedRows) {
    this.setState({ openedRows });
  }

  public onSelectAssessment = (event: React.SyntheticEvent) => {
    const target = event.target as HTMLInputElement;
    const { assessments } = this.state;
    const assessmentGroupID = target.value;
    const assessmentID = assessments.filter(
      (assessment: any) => assessment.Group.id === Number(assessmentGroupID)
    )[0].id;
    const {
      services: { adminAPI, currentUserID, currentUsername },
    } = this.props;

    this.setState({ selectedAssessment: assessmentGroupID, loading: true });

    adminAPI
      .getCurrentUserRelatedAssessmentResults({ assessmentGroupID })
      .then((results: any) => {
        const usersFilters = {};
        results.data.relations.forEach((user) => {
          usersFilters[user.id] = user.name;
        });

        adminAPI
          .getAssessmentQuestionGroups({ assessmentID })
          .then((traits: any) => {
            const traitsFilters = {};
            traits.data.groups.forEach((group) => {
              traitsFilters[group.id] = group.Title;
            });

            this.setState({
              traitsFilters,
              usersFilters,
              results: results.data.results,
              relations: results.data.relations,
              loading: false,
            });
          })
          .catch((err) => {
            alert("API error!");
          });
      })
      .catch((err) => {
        alert("API error!");
      });
  };

  public onSelectTrait = (trait: string) => (event: React.MouseEvent) => {
    let newSelectedTraits = Array();
    const { selectedTraits } = this.state;

    if (selectedTraits.includes(trait)) {
      newSelectedTraits = selectedTraits.filter((c) => c !== trait);
    } else {
      newSelectedTraits = [...selectedTraits, trait];
    }

    this.setState({ selectedTraits: newSelectedTraits });
  };

  public onSelectUser = (user: string) => async (event: React.MouseEvent) => {
    let newSelectedUsers = Array();
    const {
      services: { adminAPI },
    } = this.props;
    const {
      results,
      selectedUsers,
      selectedUsersResults,
      assessments,
      selectedAssessment,
    } = this.state;
    const assessmentGroupID = selectedAssessment;

    if (selectedUsers.includes(user)) {
      newSelectedUsers = selectedUsers.filter((c) => c !== user);
    } else {
      // make selected users mutually exclusive
      newSelectedUsers = [user];

      if (!Object.keys(selectedUsersResults).includes(user)) {

        const data = await adminAPI.getUserAssessmentResults({
          assessmentGroupID,
          userID: Number(user),
        });

        if (!data.ok) {
          toast.warn("This child has not done this assessment yet");
          return;
        }

        const assessmentResult = data.data.results;

        this.setState({
          selectedUsersResults: {
            ...selectedUsersResults,
            [user]: assessmentResult.responses,
          },
        });
      }
    }

    this.setState({ selectedUsers: newSelectedUsers });
  };

  public riskLevelColor = (score) => {
    if (score <= 24) {
      return "#e7233d";
    } else if (score <= 49) {
      return "#8e0666";
    } else if (score <= 74) {
      return "#f56f3f";
    } else if (score <= 99) {
      return "#feb743";
    }

    return "#acff24"; // or '#18ee97'
  };

  public riskLevelStyles = (groupID) => {
    const { selectedUsers, results } = this.state;

    const traitResults = results.filter((result) => {
      return (
        String(result.UserId) === String(selectedUsers[0]) &&
        String(result.GroupId) === String(groupID)
      );
    });

    const traitScore = traitResults.length
      ? traitResults[0].GroupPercentageScore
      : 100;
    const riskLevelColor = this.riskLevelColor(traitScore);
    const styles = { backgroundColor: riskLevelColor, color: "aqua" };

    if (riskLevelColor === "#acff24" || riskLevelColor === "#feb743") {
      styles.color = "black";
    }

    return styles;
  };

  public riskLevelDescription = (score) => {
    if (score <= 24) {
      return "Extremely High Alert";
    } else if (score <= 49) {
      return "High Alert";
    } else if (score <= 74) {
      return "Moderate Alert";
    } else if (score <= 99) {
      return "Low Alert";
    }

    return "No Alert";
  };

  public renderAssessmentPicker() {
    const { assessments, selectedAssessment } = this.state;

    if (assessments.length) {
      return (
        <div className="assessment-selector">
          <h3 className="title">First, pick an assessment</h3>
          <select
            name="assessment"
            data-arg="assessment"
            required={true}
            className="custom-select"
            value={selectedAssessment}
            onChange={this.onSelectAssessment}
          >
            <option value={0}>--</option>
            {assessments.map((assessment: any) => {
              return (
                <option key={assessment.Group.id} value={assessment.Group.id}>
                  {assessment.Group.Name}
                </option>
              );
            })}
          </select>
        </div>
      );
    }

    return null;
  }

  public renderTraitsFilterButtons() {
    const {
      traitsFilters,
      selectedTraits,
      selectedUsers,
      results,
    } = this.state;

    if (Object.keys(traitsFilters).length) {
      return (
        <div className="groups-filters">
          <h3 className="title">
            Responses where the system assessed an issue with
          </h3>
          <div className="ta-buttons-wrapper ta-buttons-wrapper-5">
            {Object.entries(traitsFilters).map((trait: any) => {
              const btnStyles = this.riskLevelStyles(trait[0]);

              return (
                <button
                  key={trait[0]}
                  className={`ta-button ta-button-default ta-button-small ${
                    selectedTraits.includes(trait[0]) ? "ta-button-active" : ""
                  }`}
                  onClick={this.onSelectTrait(trait[0])}
                  style={btnStyles}
                >
                  {trait[1]}
                </button>
              );
            })}
          </div>
        </div>
      );
    }

    return null;
  }

  public renderUsersFilterButtons() {
    const { usersFilters, selectedUsers } = this.state;

    if (Object.keys(usersFilters).length) {
      return (
        <div className="search-filters">
          <h3 className="title">Show me</h3>
          <div className="ta-buttons-wrapper ta-buttons-wrapper-2">
            {Object.entries(usersFilters).map((user: any) => {
              return (
                <button
                  key={user[0]}
                  className={`ta-button ${
                    selectedUsers.includes(user[0]) ? "ta-button-active" : ""
                  }`}
                  onClick={this.onSelectUser(user[0])}
                >
                  {user[1]}
                </button>
              );
            })}
          </div>
        </div>
      );
    }

    return null;
  }

  public renderAnalysisBubbleChart() {
    const pieData: any = [];
    const bubbleData: any = { color: "green", children: [] };
    const {
      selectedUsers,
      selectedUsersResults,
      selectedTraits,
      traitsFilters,
      usersFilters,
    } = this.state;

    const filteredSelectedUsersResults: any = Object.entries(
      selectedUsersResults
    ).filter((result) => selectedUsers.includes(result[0]));
    const filteredSelectedFilters = Object.keys(traitsFilters).filter((id) =>
      selectedTraits.includes(id)
    );

    if (
      filteredSelectedUsersResults.length &&
      filteredSelectedUsersResults[0].length > 0
    ) {
      bubbleData.name =
        usersFilters[Number(filteredSelectedUsersResults[0][0])];

      filteredSelectedFilters.forEach((f) => {
        bubbleData.children.push({
          name: traitsFilters[Number(f)],
          color: "red",
          children: [],
        });
      });

      filteredSelectedUsersResults[0][1].forEach((response: any) => {
        if (filteredSelectedFilters.includes(String(response.GroupId))) {
          bubbleData.children.forEach((child, i) => {
            if (child.name === traitsFilters[response.GroupId]) {
              bubbleData.children[i].children.push({
                name: `Q${response.QuestionDisplayOrder}R`,
                color: "aqua",
                score: response.Score,
              });
            }
          });
        }
      });

      // assemble the pie data here
      Object.entries(traitsFilters).map((trait: any) => {
        const sliceStyles = this.riskLevelStyles(trait[0]);
        const currentGroupData = this.state.results
          .filter(
            (res) => filteredSelectedUsersResults[0][0] === String(res.UserId)
          )
          .filter((res) => String(res.GroupId) === trait[0]);
        const sliceColor = filteredSelectedFilters.includes(
          String(currentGroupData[0].GroupId)
        )
          ? sliceStyles.backgroundColor
          : `${sliceStyles.backgroundColor}80`;

        pieData.push({
          id: trait[0],
          label: trait[1],
          value:
            currentGroupData.length && currentGroupData[0].GroupPercentageScore,
          color: sliceColor,
          textColor: sliceStyles.color,
        });
      });
    }

    if (pieData.length === 0) {
      return null;
    }

    return (
      <div className="bubble-container">
        <PieChart realData={pieData} onSliceClick={this.onSelectTrait} />

        <button className="button blue-button sf-regular-button">
          Learn More
        </button>

        {bubbleData.children.length === 0 ? null : (
          <BubbleChart
            value="score"
            identity="name"
            realData={bubbleData}
            isReady={bubbleData.children.length > 0}
          />
        )}
      </div>
    );
  }

  public renderTableRow(props) {
    const { data } = props;
    const questionData: any = [];
    const {
      traitsFilters,
      openedRows,
      results,
      selectedUsersResults,
      selectedUsers,
      selectedTraits,
    } = this.state;

    const filteredSelectedUsersResults: any = Object.entries(
      selectedUsersResults
    ).filter((result) => selectedUsers.includes(result[0]));

    if (
      filteredSelectedUsersResults.length &&
      filteredSelectedUsersResults[0].length > 0
    ) {
      filteredSelectedUsersResults[0][1]
        .filter((d) => String(d.GroupId) === data.id)
        .forEach((d: any) => {
          questionData.push({
            id: d.id,
            qnumber: `Q${d.QuestionDisplayOrder}`,
            question: d.Question,
            answer: d.AnswerValue,
            score: d.Score,
            maxScore: d.MaxPossibleScore,
          });
        });
    }

    return (
      <div className="sf-expanded-row-content">
        {questionData.map((res, i) => {
          const score = res.score / res.maxScore || 0;
          const isBelowAvg = score < 0.5;
          const key = `${i}-${res.id}`;
          const percentage = `${(score * 100).toFixed()}%`;

          return (
            <div key={key} className="sf-qa-container">
              <div className="sf-qa-no">
                <p>{res.qnumber}</p>
              </div>
              <div className="sf-qa">
                <div className="sf-qa-question">
                  <h5>Question: </h5>
                  <p>{res.question}</p>
                </div>
                <div className="sf-qa-answer">
                  <h5>Answer: </h5>
                  <p>{res.answer}</p>
                </div>
              </div>
              {/* <div className="sf-qa-score">
                <h5>Score</h5>
                <p className={isBelowAvg ? 'cf-red' : 'cf-yellow'}>{percentage}</p>
              </div> */}
            </div>
          );
        })}
      </div>
    );
  }

  public renderTable() {
    const tableData: any = [];
    const {
      traitsFilters,
      openedRows,
      results,
      selectedUsersResults,
      selectedUsers,
      selectedTraits,
    } = this.state;

    const filteredSelectedUsersResults: any = Object.entries(
      selectedUsersResults
    ).filter((result) => selectedUsers.includes(result[0]));
    const filteredSelectedFilters = Object.keys(traitsFilters).filter((id) =>
      selectedTraits.includes(id)
    );

    if (
      filteredSelectedUsersResults.length &&
      filteredSelectedUsersResults[0].length > 0
    ) {
      Object.entries(traitsFilters).map((trait: any) => {
        if (filteredSelectedFilters.includes(String(trait[0]))) {
          const currentGroupData = results
            .filter(
              (res) => filteredSelectedUsersResults[0][0] === String(res.UserId)
            )
            .filter((res) => String(res.GroupId) === trait[0]);

          tableData.push({
            id: trait[0],
            group: trait[1],
            percentage:
              currentGroupData.length &&
              `${currentGroupData[0].GroupPercentageScore}%`,
            level:
              currentGroupData.length &&
              this.riskLevelDescription(
                currentGroupData[0].GroupPercentageScore
              ),
          });
        }
      });
    }

    const columns = [
      { label: "ID", key: "id", width: 10 },
      { label: "Group", key: "group", width: 40 },
      { label: "Percentage", key: "percentage", width: 20 },
      { label: "Alert Level", key: "level", width: 30 },
    ];

    if (tableData.length === 0) {
      return null;
    }

    return (
      <ExpandableTable
        onToggle={this.onToggleRow}
        maxOpen={3}
        openedRows={openedRows}
        maxBodyHeight={450}
        renderRow={this.renderTableRow}
        columns={columns}
        data={tableData}
        className="sf-expandable-table"
      />
    );
  }

  public render() {
    const { assessments, traitsFilters, loading } = this.state;

    if (this.state.toLogin) {
      return <Redirect to="/login" />;
    }

    if (assessments.length === 0) {
      return <Loader color="#acff24" />;
    }

    return (
      <div className="ta-page">
        <Navbar logoutParent={this.onLogout} />
        <ToastContainer />
        <div className="ta-container">
          <div className="ta-content">
            <h1 className="ta-page-heading">SEARCH FILTERS</h1>
            <div className="cf-page-row cf-filters">
              {this.renderAssessmentPicker()}
              <button className="button blue-button cf-regular-button">
                Save Results
              </button>
            </div>
            {!loading ? (
              <React.Fragment>
                <div className="ta-page-row ta-filters">
                  {this.renderUsersFilterButtons()}
                  {this.renderTraitsFilterButtons()}
                </div>
                <div className="ta-page-row">
                  {this.renderAnalysisBubbleChart()}
                </div>
                <div className="ta-page-row">{this.renderTable()}</div>
              </React.Fragment>
            ) : (
              <Loader color="#acff24" />
            )}
          </div>
        </div>
      </div>
    );
  }
}

export default withServices(TraitsAnalysis);



// WEBPACK FOOTER //
// ./src/pages/DataCenter/Parent/SearchFilters/searchFilters.tsx